/**
 * The road's landmarks (Phase 2a): what they're called, where their
 * paintings load from, how big each is drawn, and where the row of them
 * under the SPIN row goes. Pure, so it can be checked at every screen size.
 */

/** Display names, by landmark id (the server sends ids). */
const NAMES: Record<string, string> = {
  fox_den: 'Fox Den',
  owls_tree: "Owl's Tree",
  old_oak: 'Old Oak',
  mushroom_ring: 'Mushroom Ring',
  moonflower_glade: 'Moonflower Glade',
  castle_gate: 'Castle Gate',
  armoury: 'Armoury',
  treasury: 'Treasury',
  throne_room: 'Throne Room',
  crown_tower: 'Crown Tower',
  saltspray_light: 'Saltspray Light',
  rock_pool: 'Rock Pool',
  sandcastle: 'Sandcastle',
  coral_arch: 'Coral Arch',
  pearl_grotto: 'Pearl Grotto',
  mountain_lodge: 'Mountain Lodge',
  knitting_hut: 'Knitting Hut',
  fishing_hut: 'Fishing Hut',
  skating_pond: 'Skating Pond',
  starlight_summit: 'Starlight Summit',
  stagecoach_stop: 'Stagecoach Stop',
  prospectors_camp: "Prospector's Camp",
  old_windmill: 'Old Windmill',
  general_store: 'General Store',
  rodeo_arena: 'Rodeo Arena',
  monorail_stop: 'Monorail Stop',
  magic_shop: 'Magic Shop',
  hover_garage: 'Hovercar Garage',
  roller_rink: 'Roller Rink',
  grand_marquee: 'Grand Marquee',
  caravan_camp: 'Caravan Camp',
  palm_well: 'Palm Well',
  lookout_rock: 'Lookout Rock',
  spice_bazaar: 'Spice Bazaar',
  sun_gate: 'Sun Gate',
  gumdrop_cottage: 'Gumdrop Cottage',
  bonbon_shop: 'Bonbon Shop',
  toffee_kitchen: 'Toffee Kitchen',
  meringue_bandstand: 'Meringue Bandstand',
  fizzing_fountain: 'Fizzing Fountain',
};

export function landmarkName(id: string): string {
  return NAMES[id] ?? id.replace(/_/g, ' ');
}

/** The texture alias of a landmark's painting, ruined or restored (the app
 *  registers them with the road's art). */
export function landmarkAlias(id: string, restored: boolean): string {
  return `road:landmark:${id}:${restored ? 'restored' : 'ruined'}`;
}

/** Each road's chapter title in the story. */
const CHAPTERS: Record<string, string> = {
  forest: 'Into the Whispering Wood',
  royal: 'The Lost Crown',
  underwater: 'After the Storm',
  frost: 'The Climb to Crystal Peak',
  frontier: 'Sundown in Dustwood',
  neon: 'The Great Switch-On',
  caravan: 'Across the Singing Sands',
  sherbet_square: 'The Fizzless Fountain',
};

/** "Chapter 1: Into the Whispering Wood". */
export function chapterTitle(themeId: string, chapter: number): string {
  const title = CHAPTERS[themeId];
  return title ? `Chapter ${chapter}: ${title}` : `Chapter ${chapter}`;
}

const WORDS = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];

/** What restoring is for, said on every restore card: `restorable`
 *  landmarks finish the chapter, which opens `next` (a slot's name). A
 *  player who already has that slot (`owned`) isn't promised it. */
export function roadPurpose(restorable: number, next: string | null, owned = false): string {
  const all = `Restore all ${WORDS[restorable] ?? restorable} landmarks to finish the chapter`;
  return next && !owned ? `${all} and open ${next} for free.` : `${all}.`;
}

/** The chapter card's line for the slot a finished chapter opened. It
 *  promises a road only when that slot has one (`road`). */
export function chapterOpened(chapter: number, next: { name: string; road: boolean }): string {
  const what = next.road ? 'a new slot with its own road to restore' : 'a new slot to play';
  return `Finishing chapter ${chapter} opened ${next.name} for you: ${what}.`;
}

/** Why the chapter card is up: the last restore just opened the next slot
 *  (`opened`), it finished the chapter but the player already had that
 *  slot (`owned`), or the player tapped a road they finished before
 *  (`again`). */
export type ChapterMoment = 'opened' | 'owned' | 'again';

/** The chapter card's title and line. Only a slot opened just now is
 *  announced as open; otherwise the card says the chapter is complete. */
export function chapterCard(
  chapter: number,
  next: { name: string; road: boolean } | null,
  moment: ChapterMoment,
): { title: string; line: string } {
  if (next && moment === 'opened') return { title: `${next.name.toUpperCase()} IS OPEN`, line: chapterOpened(chapter, next) };
  const title = `CHAPTER ${chapter} COMPLETE`;
  if (!next) return { title, line: 'Every landmark on this road is restored.' };
  if (moment === 'owned') {
    return { title, line: `Every landmark is restored. ${next.name} is already yours, so you can play it now.` };
  }
  return { title, line: `Every landmark on this road is restored. Play ${next.name} next?` };
}

/** The cost tag's height; it hangs this far below a landmark's feet, less a quarter. */
export const TAG_HEIGHT = 24;
/**
 * The largest a landmark's painted area is drawn, as the side of a square
 * of the same area.
 */
const MAX_SIDE = 84;
/** The share of its slot a landmark's painting may span, leaving a gap between neighbours. */
const SPREAD = 0.9;
/** Room kept above the tallest landmark and under the tag. */
const EDGE = 2;
/** Below this the road isn't drawn at all: the landmarks would be specks. */
export const MIN_STRIP_HEIGHT = 64;

/** The HUD row (BET, SPIN, settings) reaches this far below its centreline. */
const ROW_BELOW = 30;
const GAP = 6;
/** Room kept under the road at the bottom of the screen. */
const BOTTOM_GAP = 14;
/** "Play with others", bottom right: its box, from the right and bottom edges. */
export const PILL = { width: 200, height: 64 };

export interface StripBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * The road's box: as wide as the reel frame, from just under the SPIN row
 * down to the bottom of the screen, or to the top of "Play with others"
 * when the two would meet. Null when there isn't room for it.
 */
export function roadStripBox(
  frame: { left: number; right: number; spinRowY: number },
  viewport: { w: number; h: number },
  safeBottom = 0,
): StripBox | null {
  const left = Math.max(4, Math.round(frame.left));
  const width = Math.round(Math.min(frame.right, viewport.w - 4) - left);
  const top = Math.round(frame.spinRowY + ROW_BELOW + GAP);
  const meetsPill = left + width > viewport.w - PILL.width;
  const bottom = viewport.h - safeBottom - (meetsPill ? PILL.height + GAP : BOTTOM_GAP);
  const height = Math.round(bottom - top);
  if (width < 200 || height < MIN_STRIP_HEIGHT) return null;
  return { left, top, width, height };
}

/** How a landmark's painting fills its texture, measured from its alpha. */
export interface ArtShape {
  /** The texture's size, in pixels. */
  width: number;
  height: number;
  /** The share of its area that is painted (opaque). */
  fill: number;
  /** The painted bounds, as shares of the width and the height. */
  left: number;
  right: number;
  top: number;
  bottom: number;
  /** The painted span along its bottom rows (the ground it stands on), as shares of the width. */
  baseLeft: number;
  baseRight: number;
}

/** A landmark painting's shape before its alpha has been measured. */
export function defaultArtShape(width = 512, height = 512): ArtShape {
  return {
    width,
    height,
    fill: 0.55,
    left: 0.07,
    right: 0.93,
    top: 0.03,
    bottom: 0.97,
    baseLeft: 0.12,
    baseRight: 0.88,
  };
}

/**
 * Measure a painting from its pixels (`rgba`, `w` x `h`, as a canvas reads
 * them, any size): how much of it is painted, its painted bounds, and the
 * span of its base (the bottom tenth of the painted height). `width` and
 * `height` are the texture's own size.
 */
export function shapeFromAlpha(
  rgba: ArrayLike<number>,
  w: number,
  h: number,
  width: number,
  height: number,
  threshold = 128,
): ArtShape {
  const solid = (x: number, y: number) => rgba[(y * w + x) * 4 + 3]! >= threshold;
  let count = 0;
  let minX = w;
  let maxX = -1;
  let minY = h;
  let maxY = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (!solid(x, y)) continue;
      count++;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
  }
  if (count === 0) return defaultArtShape(width, height);
  const baseRows = Math.max(1, Math.round((maxY - minY + 1) * 0.1));
  let baseMin = w;
  let baseMax = -1;
  for (let y = maxY - baseRows + 1; y <= maxY; y++) {
    for (let x = minX; x <= maxX; x++) {
      if (!solid(x, y)) continue;
      if (x < baseMin) baseMin = x;
      if (x > baseMax) baseMax = x;
    }
  }
  return {
    width,
    height,
    fill: count / (w * h),
    left: minX / w,
    right: (maxX + 1) / w,
    top: minY / h,
    bottom: (maxY + 1) / h,
    baseLeft: baseMin / w,
    baseRight: (baseMax + 1) / w,
  };
}

/** One landmark on the road, in the road's box. */
export interface PlacedLandmark {
  /** Where its painting's centre goes. */
  x: number;
  y: number;
  /** Pixels per texture pixel. */
  scale: number;
  /** The drawn painting's larger side: the square the restore moment flies from and to. */
  size: number;
  /** Its painted bounds. */
  left: number;
  right: number;
  top: number;
  /** The middle and the width of the ground it stands on (the contact shadow, the tag). */
  baseX: number;
  baseWidth: number;
}

export interface StripLayout {
  /** The side of a square with each painting's painted area: they all match. */
  side: number;
  /** The line every landmark's feet stand on. */
  baseline: number;
  landmarks: PlacedLandmark[];
}

/**
 * Stand the landmarks in a row on one level in a `width` x `height` box,
 * evenly spaced and centred, each painting sized so its painted area
 * matches the others' (a tall tower and a wide ring look the same size),
 * all feet on one line, with room under it for the cost tag.
 *
 * `shown` is each landmark's painting as it stands now. `fit` adds the
 * paintings it may change to (ruined and restored), so the sizes and the
 * line hold still when one is restored.
 */
export function roadStripLayout(
  width: number,
  height: number,
  shown: readonly ArtShape[],
  fit: readonly ArtShape[] = [],
): StripLayout {
  const slot = width / Math.max(1, shown.length);
  const below = TAG_HEIGHT * 0.75 + EDGE;
  // A painting's scale, painted width and painted height per unit of side.
  const unit = (s: ArtShape) => 1 / Math.sqrt(s.width * s.height * Math.max(0.01, s.fill));
  const tall = (s: ArtShape) => unit(s) * s.height * (s.bottom - s.top);
  const wide = (s: ArtShape) => unit(s) * s.width * (s.right - s.left);
  const all = [...shown, ...fit];
  let side = MAX_SIDE;
  for (const s of all) {
    side = Math.min(side, (slot * SPREAD) / wide(s), (height - below - EDGE) / tall(s));
  }
  side = Math.max(1, Math.floor(side));
  const tallest = Math.max(0, ...all.map((s) => tall(s) * side));
  // The row sits in the middle of the box, the tag included.
  const top = EDGE + Math.max(0, (height - EDGE - tallest - below) / 2);
  const baseline = Math.round(top + tallest);
  const landmarks = shown.map((s, i) => {
    const scale = side * unit(s);
    const w = s.width * scale;
    const h = s.height * scale;
    // The painted middle sits in the middle of its slot; the feet on the line.
    const x = slot * (i + 0.5) - w * ((s.left + s.right) / 2 - 0.5);
    const y = baseline - h * (s.bottom - 0.5);
    return {
      x,
      y,
      scale,
      size: Math.max(w, h),
      left: x + w * (s.left - 0.5),
      right: x + w * (s.right - 0.5),
      top: y + h * (s.top - 0.5),
      baseX: x + w * ((s.baseLeft + s.baseRight) / 2 - 0.5),
      baseWidth: w * (s.baseRight - s.baseLeft),
    };
  });
  return { side, baseline, landmarks };
}
